import { React, useState, useEffect, useCallback}  from "react";
import "./BookDetailsModal.css";
import axios from "axios";

import { API_BASE_URL } from "../../Variables";
import xIcon from "../../assets/images/x-icon.svg";
import checkMark from "../../assets/images/checkmark.svg";
import StarRating from '../StarRating'; 
import AddToShelf from "../AddToShelf";
import ReviewsView from "../ReviewsView";

function BookDetailsModal( {setDisplayBookDetailsModal, bookId, refreshShelves, switchToCurrentlyReadingView} ) {
  const [book, setBook] = useState(null);
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);
  const [progressError, setProgressError] = useState(null);
  const [isInShelfError, setIsInShelfError] = useState(null);
  const [isInShelf, setIsInShelf] = useState(false);
  const [hasProgress, setHasProgress] = useState(false);
  const [openAddToShelf, setOpenAddToShelf] = useState(false);
  const token = localStorage.getItem('token');

  /**
   * Check if the book is in any shelf
   */
  const checkBookInShelf = useCallback(async () => {
    try {
      setIsInShelfError(null);
      const response = await axios.get(`${API_BASE_URL}/shelves/book/${bookId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.length > 0) {
        setIsInShelf(true);
      } else {
        setIsInShelf(false);
      }
    } catch (error) {
      setIsInShelfError("Error checking if book is in shelf");
    }
  }, [bookId, token]);

  /**
   * Open the AddToShelf modal
   */
  const handleAddToShelf = () => {
    setOpenAddToShelf(true);
  };

  /**
   * Fetch book details
   */
  useEffect(() => {
    const fetchBook = async () => {
      try {
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/books-api/${bookId}`);
        setBook(response.data.volumeInfo);
        setImage(response.data.volumeInfo.imageLinks && response.data.volumeInfo.imageLinks.thumbnail
        ? response.data.volumeInfo.imageLinks.thumbnail
        : 'https://via.placeholder.com/150');
      } catch (error) {
        setError("Error fetching book details. Please try again later.");
      }
    };

    const fetchProgress = async () => {
      try {
        setProgressError(null);
        const response = await axios.get(`${API_BASE_URL}/progress/${bookId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data && response.data.id !== undefined) { 
          setHasProgress(true);
        } else {
          setHasProgress(false);
        }
      }
      catch (error) {
        setProgressError("Error checking if book has progress");
      }
    };

    fetchBook();
    fetchProgress();
    checkBookInShelf();
  }, [bookId, token, checkBookInShelf]);

  /**
   * Check if the book is in a shelf when the AddToShelf modal is closed
   */
  useEffect(() => {
    if (!openAddToShelf) {
      checkBookInShelf();
    }
  }, [openAddToShelf, checkBookInShelf]);
  
  return (
    <div className="book-details-overlay">
      {book ? (
        <div className="book-details-modal">
          <div className="modal-close-container">
            <img src={xIcon} alt="Close" onClick={() => setDisplayBookDetailsModal(false)} className="modal-close"/>
          </div>

          {/* Book details */}
          <div className="book-details-col-1">
            <img src={image} alt={book.title} className="book-details-img"/>
            <div className="categories">
              {book.categories && book.categories.map((category, index) => (
                <p key={index} className="category-tag">{category}</p>
              ))}
            </div>
          </div>

          <div className="book-details-header">
            <h2 className="book-details-title">{book.title}</h2>
            <p>{book.authors}</p>
          </div>

          <div className="book-details-info">
            <div className="action-buttons">
              {book.averageRating !== undefined && <StarRating rating={book.averageRating} />}
              {hasProgress && <button className="secondary-button details-buttons" onClick={() => switchToCurrentlyReadingView()}>View progress</button>}
              <button className="primary-button details-buttons" onClick={() => handleAddToShelf()}> 
                {isInShelf && <img src={checkMark} alt="Checkmark" className="checkmark-icon"/>}
                Add to Shelf
              </button>
            </div>
            {progressError && <p className="error">{progressError}</p>}
            {isInShelfError && <p className="error">{isInShelfError}</p>}
            <div
              className="synopsis"
              dangerouslySetInnerHTML={{ __html: book.description }}
            />
            <hr className="divider"/>
            <ReviewsView bookId={bookId} />
          </div>

          {/* Add to Shelf modal */}
          {openAddToShelf && <AddToShelf onClose={setOpenAddToShelf} bookId={bookId} book={book} refreshShelves={refreshShelves}/>}

        </div>
      ) : (
        <div className="book-details-modal">
          <div className="modal-close-container">
            <img src={xIcon} alt="Close" onClick={() => setDisplayBookDetailsModal(false)} className="modal-close"/>
          </div>
          {error ? (
            <div className="message error">{error}</div>
          ) : (
            <p className="message">Loading...</p>
          )}
        </div>
      )}
    </div>
  );
}

export default BookDetailsModal;